import React from 'react';
import { useUuid } from '@/localShared/hooks/useUuid';
import { useVariousFetch } from '@/shared/hooks/useVariousFetcher';
import type { PretestWabikenApiResponse } from '../_type';
import type { PlayerError, PlayerProps } from '@u-next/defaultplayer';

type State = {
  pageStatus: 'INIT' | 'PLAY' | 'ERROR';
  playerProps: PlayerProps | undefined;
  errorMessage: {
    title: string;
    text: string;
    errorCode: string;
  };
};

type Action =
  | {
      type: 'INITIALIZED';
      payload: {
        uuid: string;
        apiResponse: PretestWabikenApiResponse;
      };
    }
  | {
      type: 'ERROR_OCCURRED';
      payload: {
        playerError: PlayerError;
      };
    };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'INITIALIZED': {
      const { uuid, apiResponse } = action.payload;

      const playerProps = buildPlayerPropsFromPlayInfo({ apiResponse, uuid });

      if (!playerProps) {
        return {
          ...state,
          pageStatus: 'ERROR',
          errorMessage: {
            title: '再生できません',
            text: '動画情報の取得に失敗しました。',
            errorCode: '',
          },
        };
      }

      return {
        ...state,
        pageStatus: 'PLAY',
        playerProps,
      };
    }

    case 'ERROR_OCCURRED': {
      const { playerError } = action.payload;

      return {
        ...state,
        pageStatus: 'ERROR',
        errorMessage: {
          title: playerError.customTitle ?? '',
          text: playerError.customMessage ?? '',
          errorCode: playerError.customCode
            ? String(playerError.customCode)
            : '',
        },
      };
    }
  }
};

function buildPlayerPropsFromPlayInfo({
  apiResponse,
}: {
  apiResponse: PretestWabikenApiResponse;
  uuid: string;
}): PlayerProps | null {
  if (!apiResponse.result) {
    return null;
  }

  const {
    data: { endpoints },
  } = apiResponse;

  const isJwt = endpoints[0].extra?.playToken.startsWith('Bearer ');

  return {
    UIMode: 'inline',
    playbackType: 'vod',
    playbackAuthorization: isJwt ? 'bearer' : 'playtoken',
    authorizationToken:
      (isJwt
        ? endpoints[0].extra?.playToken.replace('Bearer ', '')
        : endpoints[0].extra?.playToken) ?? '',
    endPoints: endpoints.map((endpointData) => ({
      title: 'テスト動画',
      playables: endpointData.playables.reduce((result, current) => {
        const appendValue = [
          'dash',
          'hls-cmaf',
          'hls-fp',
          'hls-s-aes',
        ].includes(current.type)
          ? {
              [current.type]: current.cdns.map((cdnData) => ({
                id: cdnData.cdnId,
                weight: cdnData.weight,
                licenseUrls:
                  cdnData.licenseUrlList?.reduce(
                    (result2, current2) => ({
                      ...result2,
                      ...{ [current2.drmType]: current2.endpoint },
                    }),
                    {}
                  ) ?? [],
                manifestUrl: cdnData.playlistUrl,
              })),
            }
          : {};

        return {
          ...result,
          ...appendValue,
        };
      }, {}),
      sceneSearchList: endpointData.sceneSearchList.reduce(
        (result, current) => ({
          ...result,
          ...{ [current.type]: current.cdns[0].sceneSearchUrl },
        }),
        {}
      ),
    })),
    autoplay: false,
    theme: {
      keyColor: 'rgba(230, 0, 50, 1)',
    },
  };
}

export type UsePretestPlayBack = {
  state: State;
  handleOnPlayerError: (error: PlayerError) => void;
};

export function usePretestPlayBack() {
  const [state, dispatch] = React.useReducer(reducer, {
    pageStatus: 'INIT',
    playerProps: undefined,
    errorMessage: {
      title: '',
      text: '',
      errorCode: '',
    },
  });

  const { uuid, isReady } = useUuid();
  const { fetcher } = useVariousFetch<PretestWabikenApiResponse>();

  const handleOnPlayerError: UsePretestPlayBack['handleOnPlayerError'] =
    React.useCallback((playerError) => {
      dispatch({
        type: 'ERROR_OCCURRED',
        payload: {
          playerError,
        },
      });
    }, []);

  React.useEffect(() => {
    switch (state.pageStatus) {
      case 'INIT': {
        if (!isReady) {
          return;
        }

        fetcher('/pretest-playback/api-wabiken', {
          method: 'POST',
          credentials: 'include',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uuid,
          }),
        }).then((apiResponse) => {
          dispatch({
            type: 'INITIALIZED',
            payload: {
              uuid,
              apiResponse,
            },
          });
        });
        return;
      }
    }
  }, [fetcher, isReady, state.pageStatus, uuid]);

  return {
    state,
    handleOnPlayerError,
  };
}
